import type { GuestReviewsResponse } from "@/hooks/use-guest-reviews";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { airbnbVillas, bookabachVillas, reviewAggregate } from "./review-platform-ratings-data";

// Static partner ratings, recorded 25 September 2026.
// Booking.com: supplied Google search screenshot, Screenshot_2026-09-25_at_11.01.10_AM_1790290897706.png.
const linkClass = "inline-flex flex-col items-center gap-1 py-2 text-sm text-white no-underline transition-colors hover:text-[#AD6433] hover:no-underline focus-visible:text-[#AD6433] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#AD6433]";
const airbnb = reviewAggregate(airbnbVillas);
const bookabach = reviewAggregate(bookabachVillas);

export default function ReviewPlatformRatings({ data, variant = "amenities" }: { data?: GuestReviewsResponse; variant?: "amenities" | "property" }) {
  const isProperty = variant === "property";
  return <div className={isProperty
    ? "mt-3 flex w-full flex-wrap items-start justify-start gap-x-6 gap-y-0 [&>a]:max-w-full [&>button]:max-w-full [&>a]:items-start [&>button]:items-start [&>a>span]:flex-wrap [&>a>span]:justify-start [&>button>span]:justify-start [&>button]:text-left"
    : "mt-4 flex flex-col items-center justify-center gap-0 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-10 sm:gap-y-0"} aria-label="Guest ratings by platform">
    {data?.reviewsUrl && <a data-testid="google-review-summary" href={data.reviewsUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>
      <span className="inline-flex items-center gap-2">
        <img src="/media/reviews/google.svg" alt="" className="h-5 w-5" />
        {data.rating !== null && <span><span className="text-[#d39a5e]">★</span> {data.rating.toFixed(1)}/5</span>}
        <span>Google</span>
        {data.totalReviews !== null && <span>(see {data.totalReviews} reviews)</span>}
      </span>
    </a>}
    {/* Separate literal labels keep visual-editor changes scoped to one platform. */}
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" data-testid="airbnb-review-summary" className={linkClass}>
          <span className="inline-flex flex-wrap items-center justify-center gap-2">
            <img src="/media/reviews/airbnb.svg" alt="" className="h-5 w-5 shrink-0 object-contain" />
            <span><span className="text-[#d39a5e]">★</span> {airbnb.score.toFixed(2)}/5</span>
            <span>Airbnb</span>
            <span>(see {airbnb.count} reviews)</span>
          </span>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Airbnb villa ratings</DialogTitle>
        <DialogDescription className="text-sm text-[color:var(--primary-foreground)]">Approximate review-weighted rating from the displayed villa scores and review counts, recorded 25 September 2026.</DialogDescription>
        <ul className="space-y-3">
          {airbnbVillas.map(villa => <li key={villa.url}>
            <a href={villa.url} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#AD6433]">
              {villa.name} — {villa.score.toFixed(2)}/5 ({villa.count} reviews) ↗
            </a>
          </li>)}
        </ul>
      </DialogContent>
    </Dialog>
    <a data-testid="booking.com-review-summary" href="https://www.booking.com/hotel/nz/toka-ridge.html" target="_blank" rel="noopener noreferrer" className={linkClass} title="Toka Ridge · Exceptional · Rating recorded 25 September 2026">
      <span className="inline-flex flex-wrap items-center justify-center gap-2">
        <img src="/media/reviews/booking.png" alt="" className="h-5 w-5 shrink-0 object-contain" />
        <span><span className="text-[#d39a5e]">★</span> 9.5/10</span>
        <span>Booking.com</span>
        <span>(see 108 reviews)</span>
      </span>
    </a>
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" data-testid="bookabach-review-summary" className={linkClass}>
          <span className="inline-flex flex-wrap items-center justify-center gap-2">
            <img src="/media/reviews/bookabach-icon.png" alt="" className="h-5 w-5 shrink-0 object-contain" />
            <span><span className="text-[#d39a5e]">★</span> {bookabach.score.toFixed(2)}/10</span>
            <span>Bookabach/VRBO</span>
            <span>(see {bookabach.count} reviews)</span>
          </span>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Bookabach villa ratings</DialogTitle>
        <DialogDescription className="text-sm text-[color:var(--primary-foreground)]">Approximate review-weighted rating from the displayed villa scores and review counts, recorded 25 September 2026. VRBO mirrors these listings and is not counted again.</DialogDescription>
        <ul className="space-y-3">
          {bookabachVillas.map(villa => <li key={villa.url}>
            <a href={villa.url} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#AD6433]">
              {villa.name} — {villa.score}/10 ({villa.count} reviews) ↗
            </a>
          </li>)}
        </ul>
      </DialogContent>
    </Dialog>
  </div>;
}